import type { HomeyAPI, User } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { __DEV__ } from '../../lib/__dev__';

export type UserProps = {
  [K in keyof User as User[K] extends Function ? never : K]: User[K];
};

type UsersData = { [key: string]: User };
type UsersById = { [key: string]: UserProps };

interface State {
  data: UsersData | null;
  byId: UsersById | null;
  isLoading: boolean;
  error: unknown;
}

export type UsersStoreState = State;

export class UsersStore extends BaseApiStore<State, User> {
  constructor() {
    super('users');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.users, reference: this });

      if (abortController.signal.aborted) return;

      const usersPromise = api.users.getUsers({ $cache: cache });

      const [usersData] = await Promise.all([usersPromise]);

      if (abortController.signal.aborted) return;

      const users = Object.values(usersData);

      const data: UsersData = {};
      const byId: UsersById = {};

      for (const userInstance of users) {
        const userReference = { ...userInstance };

        data[userReference.id] = userInstance;
        byId[userReference.id] = userReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.users, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.users.on('user.create', this.handleCreate);
    api.users.on('user.update', this.handleUpdate);
    api.users.on('user.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.users.off('user.create', this.handleCreate);
    api.users.off('user.update', this.handleUpdate);
    api.users.off('user.delete', this.handleDelete);
  }

  getInstanceFromCache(user: { id: string }) {
    return this.getCache()[user.id];
  }

  getCache(): { [key: string]: User } {
    const api = this.getApi();
    const cache = api.users.__cache['user'];
    return cache;
  }

  handleCreate = (createdUser: User) => {
    this.DEBUG && console.info(`create:user:${createdUser.id}`);
    const state = this.get();
    const userInstance = this.getInstanceFromCache(createdUser);

    if (userInstance == null) return;

    const userReference = { ...userInstance };

    this.set({
      data: {
        ...state.data,
        [createdUser.id]: userInstance,
      },
      byId: {
        ...state.byId,
        [createdUser.id]: userReference,
      },
    });
  };

  handleUpdate = (updatedUser: User) => {
    this.DEBUG && console.info(`update:user:${updatedUser.id}`);

    this.addToQueue(updatedUser.id, updatedUser);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedUser] of Object.entries(updateQueue)) {
        const userInstance = this.getInstanceFromCache(queuedUser);

        if (state.data == null) {
          this.DEBUG && console.info(`update:user:missing-data`);
          continue;
        }

        if (userInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:user:unknown:${id}`);
          continue;
        }

        const userReference = { ...userInstance };

        nextById[id] = userReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:user:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedUser: User) => {
    this.DEBUG && console.info(`delete:user:${deletedUser.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:user:missing-data`);
      return;
    }

    const userInstance = state.data[deletedUser.id];

    if (userInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[userInstance.id];
    delete nextById[userInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const users = Object.values(cache ?? {});
    const data: UsersData = {};
    const byId: UsersById = {};

    for (const userInstance of users) {
      const userReference = { ...userInstance };
      data[userReference.id] = userInstance;
      byId[userReference.id] = userReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const usersStore = new UsersStore();
